// ResetPasswordScreen — full-page screen shown when the URL has
// ?reset=<token>. Two password inputs + submit; on success shows a
// confirmation and drops the user back to the login screen.
//
// The token is a signed JWT with a 1-hour expiry and a
// `purpose: "password-reset"` claim. Validation happens server-side
// in POST /auth/reset-password; the client just forwards the token.

import React, { useState } from "react";
import {
  Lock, Eye, EyeOff, ArrowLeft, CheckCircle2, Loader2, AlertTriangle
} from "lucide-react";
import { resetPassword } from "../api";

export default function ResetPasswordScreen({ token, onDone }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");
  const [ok, setOk]     = useState(false);

  const meetsLength = pw1.length >= 6;
  const matches     = pw1.length > 0 && pw1 === pw2;
  const valid       = meetsLength && matches;

  async function submit(e) {
    e.preventDefault();
    if (busy || !valid) return;
    setErr("");
    setBusy(true);
    try {
      await resetPassword(token, pw1);
      setOk(true);
    } catch (e2) {
      setErr(e2?.message || "Couldn't reset your password. The link may have expired.");
    } finally { setBusy(false); }
  }

  return (
    <div className="reset-page">
      <div className="reset-card">
        {ok ? (
          <>
            <div className="reset-ico reset-ico--ok">
              <CheckCircle2 size={26}/>
            </div>
            <h1>Password updated</h1>
            <p>
              Your password has been changed successfully. You can now sign in
              with your new password.
            </p>
            <button type="button" className="reset-submit" onClick={onDone}>
              <ArrowLeft size={14}/> Back to sign in
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="reset-ico">
              <Lock size={22}/>
            </div>
            <h1>Set a new password</h1>
            <p>
              Choose a password you don't use anywhere else. At least six
              characters.
            </p>

            <label className="reset-field">
              <span>New password</span>
              <div className="reset-field-wrap">
                <input
                  type={show ? "text" : "password"}
                  value={pw1}
                  onChange={e => setPw1(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  minLength={6}
                />
                <button
                  type="button"
                  className="reset-field-toggle"
                  onClick={() => setShow(s => !s)}
                  aria-label={show ? "Hide passwords" : "Show passwords"}
                >{show ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
              </div>
              <small className={meetsLength || pw1.length === 0 ? "reset-hint" : "reset-hint reset-hint--bad"}>
                {meetsLength || pw1.length === 0 ? "At least 6 characters." : "Too short — needs at least 6 characters."}
              </small>
            </label>

            <label className="reset-field">
              <span>Confirm new password</span>
              <div className="reset-field-wrap">
                <input
                  type={show ? "text" : "password"}
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              {pw2.length > 0 && !matches && (
                <small className="reset-hint reset-hint--bad">Passwords don't match.</small>
              )}
            </label>

            {err && (
              <div className="reset-err">
                <AlertTriangle size={14}/> {err}
              </div>
            )}

            <button
              type="submit"
              className="reset-submit"
              disabled={!valid || busy}
            >
              {busy
                ? <><Loader2 size={16} className="reset-spin"/> Updating…</>
                : <>Update password</>}
            </button>

            <button
              type="button"
              className="reset-cancel"
              onClick={onDone}
              disabled={busy}
            >
              Cancel — back to sign in
            </button>
          </form>
        )}
      </div>
      <p className="reset-footer">
        Gabruz Universe · reset links are valid for 1&nbsp;hour
      </p>
    </div>
  );
}
